import React, { useEffect, useMemo, useRef, useState } from "react";
import { ExternalLink, Inbox } from "lucide-react";
import { useAllRiskDetails } from "../hooks/useAllRiskDetails";
import { useModuleAvailability } from "../hooks/useModuleAvailability";
import Card from "./Card";
import { BUTTON, FOCUS_RING, PRIMARY_BUTTON as PRIMARY, SEVERITY_DOT, SeverityBadge, scrollToId } from "./socUi";
import CreateJiraIssueModal from "./CreateJiraIssueModal";
import CreateServiceNowIncidentModal from "./CreateServiceNowIncidentModal";
import AssignRiskModal from "./AssignRiskModal";
import BulkAssignRiskModal from "./BulkAssignRiskModal";
import SocIncidentDetails from "./SocIncidentDetails";
import { formatAge, formatLocal } from "./socFreshness";
import {
  QUEUE_AREAS, SEVERITIES, countBy, filterQueue, severityLabel,
} from "./socQueue";

// The props the existing Jira / ServiceNow modals expect (as Fusion passes them).
export const ticketTarget = (row) => ({
  riskId: row.id,
  riskTitle: row.title,
  riskName: row.title,
  userRating: severityLabel(row.severity),
  remediationID: row.remediationId,
  remediationTitle: row.remediationTitle || row.title,
  remediationTargetDate: row.remediationTargetDate,
  progressPercentage: row.progressPercentage,
  jiraIssueKey: row.jiraIssueKey,
  jiraIssueUrl: row.jiraIssueUrl,
  serviceNowIncidentNumber: row.serviceNowIncidentNumber,
  serviceNowIncidentUrl: row.serviceNowIncidentUrl,
  remediationType: "external",
});

export const ownerLabel = (row) =>
  row.assignedToUserName || (row.invitePending ? "Invited user" : row.assignedToUserId != null ? "Assigned" : "Unassigned");

// The existing ticket for a row, as a link, or null.
export const TicketLink = ({ row }) => {
  const key = row.jiraIssueKey || row.serviceNowIncidentNumber;
  const url = row.jiraIssueKey ? row.jiraIssueUrl : row.serviceNowIncidentUrl;
  if (!key) return null;
  return url
    ? (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        title="Opens in a new tab"
        className={`inline-flex items-center gap-1 rounded text-xs font-medium text-accent-cyan hover:underline ${FOCUS_RING}`}
      >
        {key}
        <ExternalLink className="h-3 w-3" aria-hidden="true" />
      </a>
    )
    : <span className="text-xs font-medium text-content-secondary">{key}</span>;
};

// What the shared Assign dialog reads from a risk (All Risks builds the same from its rows). Every queue
// item is org-scoped (Defender XDR, attack surface, secrets, leaked credentials), so no deal context.
export const assignTarget = (row) => ({
  riskId: row.id,
  riskTitle: row.title,
  riskName: row.title,
  detailedRiskDescription: row.description,
  userRating: row.severity ? row.severity.toUpperCase() : null,
  remediationID: row.remediationId,
  remediationType: row.riskType,
  remediationTitle: row.remediationTitle || row.title,
  remediationAssignTo: row.assignedToUserId != null ? String(row.assignedToUserId) : null,
  remediationTargetDate: row.remediationTargetDate,
  progressPercentage: row.progressPercentage,
  dealId: null,
  businessUnitId: null,
});

// What the shared Bulk assign dialog reads from each risk.
export const bulkTarget = (row) => ({
  id: row.id,
  riskTitle: row.title,
  riskType: row.riskType,
  riskSeverity: row.severity ? row.severity.toUpperCase() : null,
});

// Incident queue (/soc): one row per open, assignable risk. Assigning goes through the existing
// PUT /unifiedrisks/{id}/assign (the server enforces permission). There is no release: the API has no
// unassign. Ownership is not re-checked before a write: an assign replaces whatever owner the item has
// (last write wins), and the queue reloads after every write, so it then shows the owner that stuck.
// `request` ({ tab, key }) comes from a KPI card: it switches the tab and clears the severity chip and
// selection; a new key re-applies it after the user changed filters.
// `openRequest` ({ id, key }) opens one item's details from elsewhere on the page (the feed, the
// posture ribbon); a new key re-opens it and scrolls to it.
// Details open under the row (SocIncidentDetails), the way All Risks expands a risk; the full risk
// (GET /allrisks/details, the All Risks route) is read the first time any row is opened, and again
// after the queue reloads for a write or Refresh (its changeKey), so a panel never shows a stale copy.
// `source` (a SOC area view) keeps only rows of that source. A filter the view fixes has no clear
// button: `onClearArea` is passed only where the area can be cleared.
const SocIncidentQueue = ({
  queue, area, source = null, onClearArea, onClearSource, userId, canManage, request, openRequest,
}) => {
  // Jira / ServiceNow need internet access: hidden where ticketing is unavailable, as on All Risks.
  const ticketingAvailable = useModuleAvailability().isModuleAvailable("ticketing");
  const [tab, setTab] = useState("all");
  const [severity, setSeverity] = useState("all");
  const [selected, setSelected] = useState(() => new Set());
  const [ticket, setTicket] = useState(null); // { kind: "jira" | "servicenow", row }
  // Assigning works as on every module's risk page: the shared Assign / Bulk assign dialogs (pick a
  // user or invite, optional target date, activity log), on PUT /unifiedrisks/{id}/assign.
  const [assignRow, setAssignRow] = useState(null);
  const [bulkAssign, setBulkAssign] = useState(false);
  // The open panel follows the row by id, so it shows the reloaded state after an action and goes
  // away if the item is no longer open.
  const [openId, setOpenId] = useState(null);
  const toggleOpen = (id) => setOpenId((cur) => (cur === id ? null : id));

  // The full risks from the All Risks route, read when a row is first opened and again after the
  // queue's changeKey moves (null when that read fails; the panel then shows what the queue row knows).
  const { request: requestRisks, riskFor, version: risksVersion, invalidate: invalidateRisks } =
    useAllRiskDetails({ resetKey: queue.changeKey });
  useEffect(() => {
    if (openId) requestRisks();
  }, [openId, requestRisks, risksVersion]);
  // An automatic refresh that changed the open row's owner, status or tickets re-reads its full risk,
  // so the panel never shows the old ones under the refreshed row. Nothing else re-reads: the full
  // risks are the whole organization's. A reload's own change is covered by changeKey above.
  const openRow = openId ? queue.rows.find((r) => r.id === openId) : null;
  const openSignature = openRow
    ? [openRow.assignedToUserId, openRow.invitePending, openRow.status, openRow.jiraIssueKey, openRow.serviceNowIncidentNumber].join("|")
    : null;
  const lastOpen = useRef({ id: null, signature: null, changeKey: queue.changeKey });
  useEffect(() => {
    const prev = lastOpen.current;
    lastOpen.current = { id: openId, signature: openSignature, changeKey: queue.changeKey };
    if (!openId || prev.id !== openId || prev.changeKey !== queue.changeKey) return;
    if (prev.signature !== null && openSignature !== null && prev.signature !== openSignature) invalidateRisks();
  }, [openId, openSignature, queue.changeKey, invalidateRisks]);

  useEffect(() => {
    if (!openRequest) return undefined;
    setOpenId(openRequest.id);
    // After the row has expanded, bring it into view.
    const t = setTimeout(() => scrollToId(`soc-queue-row-${openRequest.id}`), 0);
    return () => clearTimeout(t);
  }, [openRequest]);

  useEffect(() => {
    if (!request) return;
    setTab(request.tab || "all");
    setSeverity("all");
    setSelected(new Set());
  }, [request]);

  const inArea = useMemo(() => filterQueue(queue.rows, { area, source }), [queue.rows, area, source]);
  const tabRows = useMemo(() => filterQueue(inArea, { tab, userId }), [inArea, tab, userId]);
  const shown = useMemo(() => filterQueue(tabRows, { severity }), [tabRows, severity]);
  const severityCounts = countBy(tabRows, "severity");
  // Any row can be selected for bulk assign (reassigning included), by users who manage risks.
  const selectable = canManage ? shown : [];
  const selectedShown = selectable.filter((r) => selected.has(r.id));
  const allSelected = selectable.length > 0 && selectedShown.length === selectable.length;

  const tabs = [
    ["all", "All", inArea.length],
    ["unassigned", "Unassigned", filterQueue(inArea, { tab: "unassigned" }).length],
    ["mine", "Assigned to me", filterQueue(inArea, { tab: "mine", userId }).length],
  ];

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAll = () => setSelected((prev) => {
    const next = new Set(prev);
    selectable.forEach((r) => (allSelected ? next.delete(r.id) : next.add(r.id)));
    return next;
  });

  let body;
  // Before the first read lands there are no counts to show: "0 items" would read as an empty queue.
  const counting = queue.loading && queue.rows.length === 0;
  if (queue.loading && queue.rows.length === 0) {
    body = <p className="py-6 text-center text-sm text-content-muted" data-testid="soc-queue-loading">Loading incidents…</p>;
  } else if (queue.error) {
    body = (
      <div className="py-4">
        <p className="text-sm text-content-secondary">The incident queue couldn't be loaded.</p>
        <button type="button" onClick={() => queue.reload()} className={`${BUTTON} mt-2`}>Retry</button>
      </div>
    );
  } else if (shown.length === 0) {
    body = (
      <div className="flex flex-col items-center gap-2 py-8 text-center">
        <Inbox className="h-8 w-8 text-content-muted" aria-hidden="true" />
        <p className="text-sm text-content-secondary">No incidents match these filters.</p>
      </div>
    );
  } else {
    // The table scrolls inside its own box with a sticky header, so a long queue never pushes the
    // rest of the page out of reach and the column names stay in view.
    body = (
      <div className="max-h-[32rem] overflow-auto rounded-lg border border-border">
        <table className="min-w-full text-sm">
          <thead className="sticky top-0 z-10 bg-theme-card-solid shadow-[0_1px_0_var(--border-color)]">
            <tr className="text-left text-xs uppercase tracking-wide text-content-muted">
              <th className="w-8 py-2 pl-3 pr-2">
                {canManage && selectable.length > 0 && (
                  <input type="checkbox" aria-label="Select all incidents shown" checked={allSelected} onChange={toggleAll} className="cursor-pointer" />
                )}
              </th>
              <th className="py-2 pr-3">Severity</th>
              <th className="py-2 pr-3">Incident</th>
              <th className="py-2 pr-3">Age</th>
              <th className="py-2 pr-3">Status</th>
              <th className="py-2 pr-3">Source</th>
              <th className="py-2 pr-3">Assigned</th>
              <th className="py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((row) => {
              const canAssign = canManage;
              const hasTicket = !!(row.jiraIssueKey || row.serviceNowIncidentNumber);
              const isSelected = selected.has(row.id);
              const isOpen = openId === row.id;
              return (
                <React.Fragment key={row.id}>
                <tr
                  id={`soc-queue-row-${row.id}`}
                  className={`scroll-mt-12 border-t border-border align-top transition-colors ${
                    isSelected || isOpen ? "bg-accent-cyan-muted" : "hover:bg-theme-hover"
                  }`}
                  data-testid={`soc-queue-row-${row.id}`}
                >
                  <td className="py-2 pl-3 pr-2">
                    {canAssign && (
                      <input type="checkbox" aria-label={`Select ${row.title}`} checked={isSelected} onChange={() => toggle(row.id)} className="cursor-pointer" />
                    )}
                  </td>
                  <td className="py-2 pr-3">
                    <SeverityBadge severity={row.severity} />
                  </td>
                  <td className="min-w-[14rem] py-2 pr-3">
                    <button
                      type="button"
                      onClick={() => toggleOpen(row.id)}
                      aria-expanded={isOpen}
                      title={isOpen ? "Hide details" : "Show details"}
                      className={`rounded text-left font-medium text-content-primary transition-colors hover:text-accent-cyan hover:underline ${FOCUS_RING}`}
                    >
                      {row.title}
                    </button>
                    <div className="text-xs text-content-muted">{row.areaLabel}</div>
                  </td>
                  <td className="whitespace-nowrap py-2 pr-3 tabular-nums text-content-secondary" title={row.created ? formatLocal(row.created) : undefined}>
                    {row.ageMs != null ? formatAge(row.ageMs) : "—"}
                  </td>
                  <td className="whitespace-nowrap py-2 pr-3 text-content-secondary">{row.status}</td>
                  <td className="whitespace-nowrap py-2 pr-3 text-content-secondary">{row.source}</td>
                  <td className="whitespace-nowrap py-2 pr-3 text-content-secondary">{ownerLabel(row)}</td>
                  <td className="py-2">
                    <div className="flex flex-wrap items-center gap-1.5">
                      {canAssign && (
                        <button type="button" onClick={() => setAssignRow(row)} className={PRIMARY}>Assign</button>
                      )}
                      {hasTicket && <TicketLink row={row} />}
                      {!hasTicket && ticketingAvailable && canManage && row.remediationId != null && (
                        <>
                          <button type="button" onClick={() => setTicket({ kind: "jira", row })} className={BUTTON}>Jira</button>
                          <button type="button" onClick={() => setTicket({ kind: "servicenow", row })} className={BUTTON}>ServiceNow</button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
                {isOpen && (
                  <tr data-testid={`soc-queue-details-${row.id}`} className="border-t border-border bg-theme-secondary">
                    <td colSpan={8} className="px-3 py-3">
                      <SocIncidentDetails row={row} risk={riskFor(row.id)} ownerLabel={ownerLabel} />
                    </td>
                  </tr>
                )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <Card>
      <section aria-label="Incident queue" data-testid="soc-queue" id="soc-queue" className="scroll-mt-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-lg font-semibold text-content-primary">Incident queue</h2>
          <p className="text-xs text-content-muted">
            {counting ? "Loading…" : `${shown.length} ${shown.length === 1 ? "item" : "items"} · most severe first, then oldest`}
          </p>
        </div>

        <div role="tablist" aria-label="Queue view" className="mt-3 flex flex-wrap gap-1 border-b border-border">
          {tabs.map(([key, label, count]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              onClick={() => { setTab(key); setSelected(new Set()); }}
              className={`-mb-px rounded-t-md border-b-2 px-3 py-2 text-sm font-medium transition-colors ${FOCUS_RING} ${
                tab === key
                  ? "border-accent-cyan text-accent-cyan"
                  : "border-transparent text-content-secondary hover:border-border-hover hover:bg-theme-hover hover:text-content-primary"
              }`}
            >
              {counting ? label : `${label} (${count})`}
            </button>
          ))}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <div role="group" aria-label="Severity" className="flex flex-wrap gap-1">
            {["all", ...SEVERITIES].map((key) => (
              <button
                key={key}
                type="button"
                aria-pressed={severity === key}
                onClick={() => { setSeverity(key); setSelected(new Set()); }}
                className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors ${FOCUS_RING} ${
                  severity === key
                    ? "border-accent-cyan bg-accent-cyan-muted text-accent-cyan"
                    : "border-border text-content-secondary hover:border-border-hover hover:bg-theme-hover hover:text-content-primary"
                }`}
              >
                {key !== "all" && <span className={`h-1.5 w-1.5 rounded-full ${SEVERITY_DOT[key]}`} aria-hidden="true" />}
                {key === "all" ? "All" : severityLabel(key)}{counting ? "" : ` ${key === "all" ? tabRows.length : severityCounts[key] || 0}`}
              </button>
            ))}
          </div>
          {area && (
            <span className="inline-flex items-center gap-1 rounded-full bg-accent-cyan-muted px-2.5 py-1 text-xs font-medium text-accent-cyan">
              Area: {QUEUE_AREAS[area]}
              {onClearArea && (
                <button
                  type="button"
                  aria-label="Clear area filter"
                  onClick={onClearArea}
                  className={`ml-1 rounded-full px-1 leading-none transition-colors hover:bg-theme-hover ${FOCUS_RING}`}
                >
                  ×
                </button>
              )}
            </span>
          )}
          {source && (
            <span className="inline-flex items-center gap-1 rounded-full bg-accent-cyan-muted px-2.5 py-1 text-xs font-medium text-accent-cyan">
              Source: {source}
              {onClearSource && (
                <button
                  type="button"
                  aria-label="Clear source filter"
                  onClick={onClearSource}
                  className={`ml-1 rounded-full px-1 leading-none transition-colors hover:bg-theme-hover ${FOCUS_RING}`}
                >
                  ×
                </button>
              )}
            </span>
          )}
        </div>

        {queue.defenderUnavailable && !queue.error && (
          <p className="mt-3 text-xs text-content-muted" data-testid="soc-queue-defender-note">
            Defender XDR isn't connected for this organization, so no Defender XDR incidents or alerts are listed
            here. Attack-surface, secret and leaked-credential items are shown.
          </p>
        )}

        {selectedShown.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2 rounded-md bg-theme-secondary px-3 py-2 text-sm" data-testid="soc-queue-bulk">
            <span className="text-content-primary">{selectedShown.length} selected</span>
            <button type="button" onClick={() => setBulkAssign(true)} className={PRIMARY}>
              {`Assign ${selectedShown.length}`}
            </button>
            <button type="button" onClick={() => setSelected(new Set())} className={BUTTON}>Clear selection</button>
          </div>
        )}

        <div className="mt-3">{body}</div>
      </section>

      {assignRow && (
        <AssignRiskModal
          show
          risk={assignTarget(assignRow)}
          riskId={assignRow.id}
          riskType={assignRow.riskType}
          assignEndpoint={`/unifiedrisks/${assignRow.id}/assign`}
          onClose={() => setAssignRow(null)}
          refreshData={() => queue.reload()}
        />
      )}
      {bulkAssign && (
        <BulkAssignRiskModal
          show
          risks={selectedShown.map(bulkTarget)}
          riskType={null}
          onClose={() => { setBulkAssign(false); setSelected(new Set()); }}
          refreshData={() => queue.reload()}
        />
      )}
      {ticket?.kind === "jira" && (
        <CreateJiraIssueModal
          isOpen
          onClose={() => setTicket(null)}
          remediation={ticketTarget(ticket.row)}
          remediationType="external"
          onSuccess={() => { setTicket(null); queue.reload(); }}
        />
      )}
      {ticket?.kind === "servicenow" && (
        <CreateServiceNowIncidentModal
          isOpen
          onClose={() => setTicket(null)}
          remediation={ticketTarget(ticket.row)}
          remediationType="external"
          onSuccess={() => { setTicket(null); queue.reload(); }}
        />
      )}
    </Card>
  );
};

export default SocIncidentQueue;
